/**
 * Anchored floating surface (menus, pickers, the jobs dropdown). Portalled, closes on
 * outside pointer-down, Escape (focus returns to the anchor) or focus leaving it; pauses
 * page hotkeys (`"popover"` scope) while open.
 */
import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeyScope } from '@/lib/hotkeys';
import { useAnchoredPosition, type Placement } from './floating';
import { getFocusable } from './focus';
import styles from './Popover.module.css';

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  placement?: Placement;
  children: ReactNode;
  className?: string;
  id?: string;
  /** ARIA role of the surface (default `"dialog"`). */
  role?: 'dialog' | 'menu' | 'listbox';
  'aria-label'?: string;
  /** Focus the first focusable child on open (default `true`). */
  autoFocus?: boolean;
}

export function Popover({
  open,
  onClose,
  anchorRef,
  placement = 'bottom-start',
  children,
  className,
  id,
  role = 'dialog',
  'aria-label': ariaLabel,
  autoFocus = true,
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const style = useAnchoredPosition(anchorRef, ref, open, placement);
  useHotkeyScope('popover', open);

  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (open && autoFocus && ref.current) (getFocusable(ref.current)[0] ?? ref.current).focus();
  }, [open, autoFocus]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onCloseRef.current();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div
      ref={ref}
      id={id}
      role={role}
      aria-label={ariaLabel}
      tabIndex={-1}
      className={clsx(styles.popover, className)}
      style={style}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
          anchorRef.current?.focus();
        }
      }}
      onBlur={(e) => {
        const next = e.relatedTarget as Node | null;
        if (next && !ref.current?.contains(next) && !anchorRef.current?.contains(next)) onClose();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
